import type {
  ReportViewConfig,
  ReportViewConfigDto,
  ReportViewConfigUpsertAck,
  UpsertReportViewConfig,
} from './reportViewConfig.types'

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function nullableString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value : null
}

function sortOrderString(value: unknown): string | null {
  if (value === 1 || value === -1) return String(value)
  return nullableString(value)
}

function stringList(value: unknown): string[] {
  if (!Array.isArray(value)) return []
  const seen = new Set<string>()
  return value.flatMap((item) => {
    if (typeof item !== 'string') return []
    const next = item.trim()
    if (!next || seen.has(next)) return []
    seen.add(next)
    return [next]
  })
}

function mediaColumns(value: unknown): ReportViewConfig['mediaColumns'] {
  if (typeof value === 'string') return value
  if (Array.isArray(value)) return stringList(value)
  return null
}

export function parseReportViewConfig(value: unknown): ReportViewConfig | null {
  if (!isRecord(value) || typeof value.table_id !== 'string' || !value.table_id.trim()) return null
  return {
    tableId: value.table_id.trim(),
    dimensions: stringList(value.dimensions),
    metrics: stringList(value.metrics),
    sortField: nullableString(value.sortField),
    sortOrder: sortOrderString(value.sortOrder),
    breakdownColumn: nullableString(value.breakdownColumn),
    mediaColumns: mediaColumns(value.mediaColumns),
    lineMetric: nullableString(value.lineMetric),
    checkLine: nullableString(value.checkLine),
    activeChart: nullableString(value.activeChart),
    groupColumn: nullableString(value.groupColumn),
  }
}

/** Normalize the backend's exceptional bare-array response and drop malformed/duplicate rows. */
export function parseReportViewConfigs(value: unknown): ReportViewConfig[] {
  if (!Array.isArray(value)) throw new TypeError('Malformed table view config response')
  const byId = new Map<string, ReportViewConfig>()
  for (const item of value) {
    const parsed = parseReportViewConfig(item)
    if (parsed && !byId.has(parsed.tableId)) byId.set(parsed.tableId, parsed)
  }
  return [...byId.values()]
}

export function serializeReportViewConfig(input: UpsertReportViewConfig): ReportViewConfigDto {
  const tableId = input.tableId.trim()
  if (!tableId) throw new TypeError('tableId is required')
  return {
    workspace_id: input.workspaceId,
    table_id: tableId,
    dimensions: stringList(input.dimensions),
    metrics: stringList(input.metrics),
    sortField: input.sortField?.trim() ?? '',
    sortOrder: input.sortOrder?.trim() ?? '',
    breakdownColumn: input.breakdownColumn?.trim() ?? '',
    mediaColumns: input.mediaColumns ?? [],
    lineMetric: input.lineMetric?.trim() ?? '',
    checkLine: input.checkLine?.trim() ?? '',
    activeChart: input.activeChart?.trim() ?? '',
    groupColumn: input.groupColumn?.trim() ?? '',
  }
}

export function parseReportViewConfigUpsertAck(value: unknown): ReportViewConfigUpsertAck {
  if (
    !isRecord(value) ||
    (value.status !== 'created' && value.status !== 'updated') ||
    typeof value.table_id !== 'string' ||
    !value.table_id.trim()
  ) {
    throw new TypeError('Malformed table view config acknowledgement')
  }
  return { status: value.status, tableId: value.table_id.trim() }
}
